import React, { useEffect, useState, useMemo } from "react";
import { api, formatError, romeDateString } from "@/lib/api";
import { useNavigate } from "react-router-dom";
import { useWS } from "@/contexts/WebSocketContext";
import { toast } from "sonner";
import { Calculator, Receipt, X, FileCheck2, Send, Banknote, Plus, Minus, Divide, Equal, Delete, RefreshCw, ShoppingBag, Coffee, Trash2, Printer } from "lucide-react";
import { tryDirectPrint } from "@/lib/fastPrint";

const STATUS_LABEL = {
    libero: "Libero", prenotato: "Prenotato", occupato: "In Servizio",
    in_attesa: "In Cucina", pronti: "Pronti", conto: "Conto",
};

const TerminaleCassa = () => {
    const [tables, setTables] = useState([]);
    const [orders, setOrders] = useState([]);
    const [rooms, setRooms] = useState([]);
    const [activeRoomId, setActiveRoomId] = useState("all"); // "all" mostra tutto
    const [showCalc, setShowCalc] = useState(false);
    const [showClosure, setShowClosure] = useState(false);
    const [showQuickSale, setShowQuickSale] = useState(false);
    const navigate = useNavigate();
    const { lastEvent } = useWS();

    const load = async () => {
        const [t, o, r] = await Promise.all([api.get("/tables"), api.get("/orders"), api.get("/rooms")]);
        setTables(t.data);
        setOrders(o.data);
        setRooms(r.data);
    };

    useEffect(() => { load(); const id = setInterval(load, 6000); return () => clearInterval(id); }, []);
    useEffect(() => {
        if (!lastEvent) return;
        // ⚠️ Includere `new_kitchen_order` per refresh immediato quando un tablet/cellulare
        // invia una nuova comanda (senza dover aspettare il polling 6s).
        if (["bill_requested", "table_updated", "payment_completed", "new_kitchen_order", "order_items_ready"].includes(lastEvent.type)) load();
    }, [lastEvent]);

    const activeOrder = (tableId) => orders.find((o) => o.table_id === tableId && o.status !== "chiuso");
    // Total = piatti (price + extras) × qty, MENO sconti per item, MENO sconto globale.
    // Coperto NON incluso (viene calcolato in CassaDetail con il menu attivo del momento del pagamento).
    const orderTotal = (o) => {
        if (!o) return 0;
        let sub = 0;
        for (const it of (o.items || [])) {
            const extrasSum = (it.extras || []).reduce((s, e) => s + (parseFloat(e.price) || 0), 0);
            const gross = (it.price + extrasSum) * it.quantity;
            let net = gross;
            if (it.discount_pct) net = net * (1 - it.discount_pct / 100);
            if (it.discount_amt) net = Math.max(0, net - it.discount_amt);
            sub += net;
        }
        if (o.discount_pct) sub = sub * (1 - o.discount_pct / 100);
        if (o.discount_amt) sub = Math.max(0, sub - o.discount_amt);
        return sub;
    };

    const archiveOrder = async (e, order) => {
        e.stopPropagation();
        if (!order) return;
        if (!window.confirm("Mettere in archivio questo ordine? Il tavolo verrà liberato.")) return;
        try {
            await api.post(`/orders/${order.id}/archive`);
            toast.success("Ordine archiviato");
            load();
        } catch (err) { toast.error(formatError(err)); }
    };

    const reprintOrder = async (e, order) => {
        e.stopPropagation();
        if (!order) return;
        if (!window.confirm(`Ristampare la comanda del tavolo ${order.table_id ? "" : ""}?\nVerrà ristampato l'intero ordine su tutte le destinazioni (cucina/bar/pizza).`)) return;
        try {
            const { data } = await api.post(`/orders/${order.id}/reprint`, {});
            if (data?.duplicate) {
                toast.info("Ristampa già richiesta poco fa, attendi qualche secondo");
                return;
            }
            toast.success(`Ristampa in corso (${data.items_count} articoli)`);
            // ⚠️ FAST-PATH: stampa DIRETTA dal browser corrente (questo PC cassa
            // ha tipicamente la stampante pairata). Identico flusso del "Test Diretto".
            const dps = data?.direct_payloads || [];
            if (dps.length > 0) {
                tryDirectPrint(dps).catch(err => console.warn("fast-print reprint:", err));
            }
        } catch (err) { toast.error(formatError(err)); }
    };

    const occupied = tables.filter((t) => ["occupato", "in_attesa", "pronti", "conto"].includes(t.status));
    const free = tables.filter((t) => t.status === "libero" || t.status === "prenotato");
    // Ordini "BIS in attesa di pagamento": ordine già chiuso come sessione (awaiting_payment=true)
    // ma NON pagato. Visualizzati come pseudo-tavoli per permettere al cassiere di chiuderli.
    const awaitingPaymentOrders = orders.filter((o) => o.awaiting_payment === true && o.status !== "chiuso");
    const rmap = useMemo(() => Object.fromEntries(rooms.map((r) => [r.id, r])), [rooms]);
    // Filtro per sala attiva
    const occupiedFiltered = activeRoomId === "all" ? occupied : occupied.filter((t) => t.room_id === activeRoomId);
    const freeFiltered = activeRoomId === "all" ? free : free.filter((t) => t.room_id === activeRoomId);
    // Conteggi per sala (badge)
    const roomCounts = useMemo(() => {
        const m = {};
        for (const t of occupied) m[t.room_id || "_"] = (m[t.room_id || "_"] || 0) + 1;
        return m;
    }, [occupied]);

    return (
        <div className="space-y-6" data-testid="cassa-page">
            <div className="flex items-start justify-between flex-wrap gap-4">
                <div>
                    <div className="text-xs uppercase tracking-[0.3em] text-brand mb-2">Pagamenti</div>
                    <h1 className="font-display text-4xl font-bold">Terminale Cassa</h1>
                </div>
                <div className="flex gap-2 flex-wrap">
                    <button onClick={() => setShowQuickSale(true)} data-testid="open-quick-sale"
                        className="h-12 px-5 bg-amber-600 hover:bg-amber-500 text-white font-bold rounded-md flex items-center gap-2 transition-colors">
                        <Coffee className="w-4 h-4" /> Conto Libero / Banco
                    </button>
                    <button onClick={() => setShowCalc(true)} data-testid="open-calculator"
                        className="h-12 px-5 border border-[#1f2c44] hover:border-brand text-elegant hover:text-brand rounded-md flex items-center gap-2 transition-colors">
                        <Calculator className="w-4 h-4" /> Calcolatrice
                    </button>
                    <button onClick={() => setShowClosure(true)} data-testid="open-closure"
                        className="h-12 px-5 bg-brand text-deep font-bold rounded-md flex items-center gap-2 hover:bg-brand-light transition-colors">
                        <FileCheck2 className="w-4 h-4" /> Chiusura Corrispettivi
                    </button>
                </div>
            </div>

            {rooms.length > 1 && (
                <div className="flex flex-wrap gap-2" data-testid="cassa-room-tabs">
                    <button
                        onClick={() => setActiveRoomId("all")}
                        data-testid="cassa-room-all"
                        className={`px-4 h-11 rounded-md border text-sm font-medium flex items-center gap-2 ${
                            activeRoomId === "all" ? "border-brand bg-brand/10 text-brand" : "border-[#1f2c44] text-elegant hover:border-brand"
                        }`}
                    >
                        Tutte le sale
                        <span className={`px-2 py-0.5 rounded text-[10px] font-bold ${activeRoomId === "all" ? "bg-brand text-deep" : "bg-deep text-elegant"}`}>
                            {occupied.length}
                        </span>
                    </button>
                    {rooms.map((r) => {
                        const cnt = roomCounts[r.id] || 0;
                        const isActive = activeRoomId === r.id;
                        return (
                            <button
                                key={r.id}
                                onClick={() => setActiveRoomId(r.id)}
                                data-testid={`cassa-room-${r.id}`}
                                className={`px-4 h-11 rounded-md border text-sm font-medium flex items-center gap-2 ${
                                    isActive ? "border-brand bg-brand/10 text-brand" : "border-[#1f2c44] text-elegant hover:border-brand"
                                }`}
                            >
                                {r.name}
                                {cnt > 0 && (
                                    <span className={`px-2 py-0.5 rounded text-[10px] font-bold ${isActive ? "bg-brand text-deep" : "bg-amber-500 text-deep"}`}>
                                        {cnt}
                                    </span>
                                )}
                            </button>
                        );
                    })}
                </div>
            )}

            {awaitingPaymentOrders.length > 0 && (
                <div data-testid="awaiting-payment-section">
                    <div className="text-xs uppercase tracking-wider text-amber-300 mb-3 flex items-center gap-2">
                        <span className="bg-amber-500/20 border border-amber-500/60 px-2 py-1 rounded text-amber-200 font-bold">⚠ Conti BIS in attesa di pagamento</span>
                        <span className="text-elegant">({awaitingPaymentOrders.length})</span>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
                        {awaitingPaymentOrders.map((o) => {
                            const t = tables.find((tx) => tx.id === o.table_id) || {};
                            if (activeRoomId !== "all" && t.room_id !== activeRoomId) return null;
                            const total = orderTotal(o);
                            const room = t.room_id ? rmap[t.room_id] : null;
                            return (
                                <div key={o.id}
                                    onClick={() => navigate(`/cassa/${t.id}?order=${o.id}`)}
                                    data-testid={`awaiting-card-${o.id}`}
                                    className="bg-amber-900/15 border-2 border-amber-500/60 rounded-lg p-4 cursor-pointer hover:border-amber-400 hover:bg-amber-900/25 transition-colors">
                                    <div className="flex items-start justify-between mb-2">
                                        <div>
                                            <div className="font-display text-2xl font-bold text-amber-200">
                                                Tavolo {o.bis_label || t.number || "?"}
                                            </div>
                                            <div className="text-[10px] uppercase tracking-wider text-amber-400 flex items-center gap-1 mt-0.5">
                                                <span className="bg-amber-500/30 px-1.5 py-0.5 rounded">SESSIONE CHIUSA</span>
                                                {room && <span className="text-elegant ml-1">· {room.name}</span>}
                                            </div>
                                        </div>
                                        <div className="text-right">
                                            <div className="text-[10px] uppercase tracking-wider text-elegant">Totale</div>
                                            <div className="font-display text-xl font-bold text-amber-200">€ {total.toFixed(2)}</div>
                                        </div>
                                    </div>
                                    <div className="text-xs text-elegant">
                                        {(o.items || []).filter((it) => it.status !== "annullato").length} piatti · cameriere {o.waiter_name || "—"}
                                    </div>
                                    {o.released_for_bis_by && (
                                        <div className="text-[10px] text-amber-300/80 italic mt-1">
                                            Liberato da {o.released_for_bis_by} per nuovo turno
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                </div>
            )}

            <div>
                <div className="text-xs uppercase tracking-wider text-elegant mb-3">
                    Tavoli aperti ({occupiedFiltered.length})
                    {activeRoomId !== "all" && rmap[activeRoomId] && (
                        <span className="ml-2 text-brand">· {rmap[activeRoomId].name}</span>
                    )}
                </div>
                {occupiedFiltered.length === 0 && <div className="bg-surface border border-[#1f2c44] rounded-lg p-8 text-center text-elegant">Nessun tavolo aperto{activeRoomId !== "all" && rmap[activeRoomId] ? ` in "${rmap[activeRoomId].name}"` : ""}.</div>}
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                    {occupiedFiltered.map((t) => {
                        const o = activeOrder(t.id);
                        const total = orderTotal(o);
                        const room = rmap[t.room_id];
                        return (
                            <div key={t.id} onClick={() => navigate(`/cassa/${t.id}`)} data-testid={`cassa-table-${t.number}`}
                                role="button" tabIndex={0}
                                className={`relative cursor-pointer text-left p-5 rounded-lg border-2 status-${t.status} hover:scale-[1.02] transition-transform`}>
                                <div className="flex justify-between items-start mb-3">
                                    <div>
                                        <div className="text-[10px] uppercase tracking-wider opacity-70">Tavolo</div>
                                        <div className="font-display text-4xl font-bold leading-tight">{t.number}</div>
                                        {room && activeRoomId === "all" && (
                                            <div className="text-[10px] uppercase tracking-[0.2em] opacity-70 mt-0.5">{room.name}</div>
                                        )}
                                    </div>
                                    <div className="text-right">
                                        <div className="text-[10px] uppercase tracking-wider opacity-70">Totale</div>
                                        <div className="font-display text-2xl font-bold">€ {total.toFixed(2)}</div>
                                    </div>
                                </div>
                                <div className="text-xs uppercase tracking-wider font-bold">{STATUS_LABEL[t.status]}</div>
                                {o && <div className="text-[11px] mt-1 opacity-80">{o.items.length} piatti · {o.waiter_name}</div>}
                                {o && (
                                    <div className="absolute top-3 right-3 flex flex-col gap-1">
                                        <button
                                            onClick={(e) => reprintOrder(e, o)}
                                            data-testid={`reprint-order-${o.id}`}
                                            title="Ristampa comanda (cucina/bar/pizza)"
                                            className="h-8 w-8 rounded-md bg-deep/40 hover:bg-brand/20 border border-brand/30 hover:border-brand text-brand hover:text-brand-light flex items-center justify-center transition"
                                        >
                                            <Printer className="w-3.5 h-3.5" />
                                        </button>
                                        <button
                                            onClick={(e) => archiveOrder(e, o)}
                                            data-testid={`archive-order-${o.id}`}
                                            title="Metti in archivio (libera tavolo)"
                                            className="h-8 w-8 rounded-md bg-deep/40 hover:bg-red-500/20 border border-red-500/30 hover:border-red-500 text-red-400 hover:text-red-300 flex items-center justify-center transition"
                                        >
                                            <Trash2 className="w-3.5 h-3.5" />
                                        </button>
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
            </div>

            <div>
                <div className="text-xs uppercase tracking-wider text-elegant mb-3">Tavoli liberi ({freeFiltered.length})</div>
                <div className="grid grid-cols-3 sm:grid-cols-6 lg:grid-cols-8 gap-2">
                    {freeFiltered.map((t) => (
                        <div key={t.id} className={`status-${t.status} border rounded p-2 text-center text-xs`}>
                            <div className="font-display font-bold text-lg">{t.number}</div>
                        </div>
                    ))}
                </div>
            </div>

            {showCalc && <CalculatorModal onClose={() => setShowCalc(false)} />}
            {showClosure && <ClosureModal onClose={() => setShowClosure(false)} />}
            {showQuickSale && <QuickSaleModal onClose={() => setShowQuickSale(false)} onPaid={load} />}
        </div>
    );
};

// ─────────────────── CALCOLATRICE ───────────────────
const CalculatorModal = ({ onClose }) => {
    const [display, setDisplay] = useState("0");
    const [acc, setAcc] = useState(null);
    const [op, setOp] = useState(null);
    const [waitingNew, setWaitingNew] = useState(false);

    const inputDigit = (d) => {
        if (waitingNew) { setDisplay(String(d)); setWaitingNew(false); return; }
        setDisplay(display === "0" ? String(d) : display + d);
    };
    const inputDot = () => {
        if (waitingNew) { setDisplay("0."); setWaitingNew(false); return; }
        if (!display.includes(".")) setDisplay(display + ".");
    };
    const clearAll = () => { setDisplay("0"); setAcc(null); setOp(null); setWaitingNew(false); };
    const negate = () => setDisplay(String(-parseFloat(display)));
    const percent = () => setDisplay(String(parseFloat(display) / 100));
    const backspace = () => setDisplay(display.length > 1 ? display.slice(0, -1) : "0");

    const compute = (a, b, o) => {
        if (o === "+") return a + b;
        if (o === "-") return a - b;
        if (o === "*") return a * b;
        if (o === "/") return b === 0 ? 0 : a / b;
        return b;
    };

    const setOperation = (newOp) => {
        const cur = parseFloat(display);
        if (acc !== null && op && !waitingNew) {
            const r = compute(acc, cur, op);
            setDisplay(String(parseFloat(r.toFixed(4))));
            setAcc(r);
        } else setAcc(cur);
        setOp(newOp);
        setWaitingNew(true);
    };
    const equals = () => {
        if (acc === null || op === null) return;
        const r = compute(acc, parseFloat(display), op);
        setDisplay(String(parseFloat(r.toFixed(4))));
        setAcc(null); setOp(null); setWaitingNew(true);
    };

    const restoFrom = (banconota) => {
        const totale = parseFloat(display) || 0;
        const resto = banconota - totale;
        setDisplay(String(parseFloat(resto.toFixed(2))));
        toast.success(`Resto da € ${banconota}: € ${resto.toFixed(2)}`);
        setWaitingNew(true);
    };

    const Btn = ({ onClick, children, className = "", testId }) => (
        <button onClick={onClick} data-testid={testId}
            className={`h-14 rounded-md font-display font-bold text-lg active:scale-95 transition-transform ${className}`}>
            {children}
        </button>
    );

    return (
        <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4 backdrop-blur-sm" onClick={onClose}>
            <div onClick={(e) => e.stopPropagation()} className="bg-surface border border-[#1f2c44] rounded-2xl p-6 max-w-md w-full" data-testid="calculator-modal">
                <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-2">
                        <Calculator className="w-5 h-5 text-brand" />
                        <div className="font-display text-xl font-bold">Calcolatrice</div>
                    </div>
                    <button onClick={onClose} className="text-elegant hover:text-warm" data-testid="close-calc"><X /></button>
                </div>

                <div className="bg-deep border-2 border-[#1f2c44] rounded-md p-4 mb-3 text-right" data-testid="calc-display">
                    <div className="text-elegant text-xs h-4">{acc !== null && op ? `${acc} ${op}` : ""}</div>
                    <div className="font-display font-bold text-4xl text-brand truncate">{display}</div>
                </div>

                {/* Resto da banconota */}
                <div className="grid grid-cols-4 gap-2 mb-3">
                    {[10, 20, 50, 100].map((b) => (
                        <Btn key={b} onClick={() => restoFrom(b)} testId={`resto-${b}`}
                            className="bg-brand-soft border border-brand/40 text-brand text-sm">
                            Resto €{b}
                        </Btn>
                    ))}
                </div>

                {/* Tastierino */}
                <div className="grid grid-cols-4 gap-2">
                    <Btn onClick={clearAll} className="bg-red-700 hover:bg-red-600 text-white" testId="calc-ac">AC</Btn>
                    <Btn onClick={negate} className="bg-surface-elevated border border-[#1f2c44] hover:border-brand text-warm">±</Btn>
                    <Btn onClick={percent} className="bg-surface-elevated border border-[#1f2c44] hover:border-brand text-warm">%</Btn>
                    <Btn onClick={() => setOperation("/")} className={`text-white ${op === "/" ? "bg-brand text-deep" : "bg-amber-700 hover:bg-amber-600"}`}><Divide className="w-5 h-5 mx-auto" /></Btn>

                    <Btn onClick={() => inputDigit(7)} className="bg-deep border border-[#1f2c44] hover:border-brand">7</Btn>
                    <Btn onClick={() => inputDigit(8)} className="bg-deep border border-[#1f2c44] hover:border-brand">8</Btn>
                    <Btn onClick={() => inputDigit(9)} className="bg-deep border border-[#1f2c44] hover:border-brand">9</Btn>
                    <Btn onClick={() => setOperation("*")} className={`text-white ${op === "*" ? "bg-brand text-deep" : "bg-amber-700 hover:bg-amber-600"}`}>×</Btn>

                    <Btn onClick={() => inputDigit(4)} className="bg-deep border border-[#1f2c44] hover:border-brand">4</Btn>
                    <Btn onClick={() => inputDigit(5)} className="bg-deep border border-[#1f2c44] hover:border-brand">5</Btn>
                    <Btn onClick={() => inputDigit(6)} className="bg-deep border border-[#1f2c44] hover:border-brand">6</Btn>
                    <Btn onClick={() => setOperation("-")} className={`text-white ${op === "-" ? "bg-brand text-deep" : "bg-amber-700 hover:bg-amber-600"}`}><Minus className="w-5 h-5 mx-auto" /></Btn>

                    <Btn onClick={() => inputDigit(1)} className="bg-deep border border-[#1f2c44] hover:border-brand">1</Btn>
                    <Btn onClick={() => inputDigit(2)} className="bg-deep border border-[#1f2c44] hover:border-brand">2</Btn>
                    <Btn onClick={() => inputDigit(3)} className="bg-deep border border-[#1f2c44] hover:border-brand">3</Btn>
                    <Btn onClick={() => setOperation("+")} className={`text-white ${op === "+" ? "bg-brand text-deep" : "bg-amber-700 hover:bg-amber-600"}`}><Plus className="w-5 h-5 mx-auto" /></Btn>

                    <Btn onClick={() => inputDigit(0)} className="col-span-2 bg-deep border border-[#1f2c44] hover:border-brand">0</Btn>
                    <Btn onClick={inputDot} className="bg-deep border border-[#1f2c44] hover:border-brand">.</Btn>
                    <Btn onClick={equals} className="bg-brand hover:bg-brand-light text-deep" testId="calc-equals"><Equal className="w-5 h-5 mx-auto" /></Btn>
                </div>

                <button onClick={backspace} className="w-full h-10 mt-2 border border-[#1f2c44] hover:border-amber-500 text-elegant hover:text-amber-400 rounded-md flex items-center justify-center gap-2 text-sm">
                    <Delete className="w-4 h-4" /> Cancella ultima cifra
                </button>
            </div>
        </div>
    );
};

// ─────────────────── CHIUSURA CORRISPETTIVI ───────────────────
const ClosureModal = ({ onClose }) => {
    const [date, setDate] = useState(romeDateString());
    const [preview, setPreview] = useState(null);
    const [loading, setLoading] = useState(false);
    const [closing, setClosing] = useState(false);
    const [history, setHistory] = useState([]);
    const [showHistory, setShowHistory] = useState(false);
    const [notes, setNotes] = useState("");

    const loadPreview = async (d) => {
        setLoading(true);
        try {
            const { data } = await api.get(`/payments/closure-preview?date=${d}`);
            setPreview(data);
        } catch (e) { toast.error(formatError(e)); }
        setLoading(false);
    };

    const loadHistory = async () => {
        const { data } = await api.get("/payments/closures?limit=30");
        setHistory(data);
    };

    useEffect(() => { loadPreview(date); /* eslint-disable-next-line */ }, [date]);
    useEffect(() => { loadHistory(); }, []);

    const closeDay = async (force = false) => {
        setClosing(true);
        try {
            const { data } = await api.post("/payments/closure", { date, notes, force });
            toast.success(data.message);
            await loadPreview(date);
            await loadHistory();
        } catch (e) {
            const msg = e?.response?.data?.detail || formatError(e);
            if (e?.response?.status === 409) {
                if (window.confirm(`${msg}\n\nVuoi ri-emettere la chiusura?`)) {
                    await closeDay(true);
                    return;
                }
            } else toast.error(msg);
        }
        setClosing(false);
    };

    const printReceipt = () => {
        if (!preview?.closure && !preview) return;
        const c = preview.closure || preview;
        const data = preview.summary;
        const win = window.open("", "_blank");
        if (!win) { toast.error("Popup bloccato"); return; }
        const methodRows = (preview.by_method || []).map((m) =>
            `<tr><td>${m.method}</td><td style="text-align:center">${m.count}</td><td style="text-align:right">€ ${m.amount.toFixed(2)}</td></tr>`
        ).join("");
        win.document.write(`<!doctype html><html><head><title>Chiusura Z${c.z_number || "—"}</title><style>
        @page { size: 80mm auto; margin: 4mm; }
        body { font-family: 'Courier New', monospace; font-size: 10pt; max-width: 72mm; margin: 0 auto; }
        h1 { font-size: 12pt; text-align: center; margin: 0 0 4mm; letter-spacing: 2pt; }
        .info { font-size: 9pt; text-align: center; margin-bottom: 4mm; }
        table { width: 100%; border-collapse: collapse; margin: 3mm 0; font-size: 9pt; }
        th, td { padding: 1mm 0; text-align: left; }
        .total { font-size: 14pt; font-weight: bold; text-align: right; border-top: 2px solid #000; padding-top: 2mm; margin-top: 3mm; }
        .footer { font-size: 8pt; text-align: center; margin-top: 5mm; border-top: 1px dashed #000; padding-top: 3mm; }
        .toolbar { padding: 8px; text-align: center; }
        .toolbar button { padding: 6px 14px; font-size: 12px; background: #2BA889; color: #fff; border: 0; border-radius: 4px; cursor: pointer; margin: 0 4px; }
        @media print { .toolbar { display: none; } }
        </style></head><body>
        <div class="toolbar"><button onclick="window.print()">Stampa</button><button onclick="window.close()" style="background:#333">Chiudi</button></div>
        <h1>CHIUSURA Z${c.z_number || "—"}</h1>
        <div class="info">Smart Table by renova360<br/>${c.date || preview.date}<br/>Operatore: ${c.operator_name || "—"}</div>
        <table><tr><th>Metodo</th><th style="text-align:center">N°</th><th style="text-align:right">Importo</th></tr>${methodRows}</table>
        <table>
            <tr><td>Documenti emessi:</td><td style="text-align:right">${data.documents_count}</td></tr>
            <tr><td>Scontrini:</td><td style="text-align:right">${data.scontrini_count}</td></tr>
            <tr><td>Fatture:</td><td style="text-align:right">${data.fatture_count}</td></tr>
            <tr><td>Sconti:</td><td style="text-align:right">€ ${data.total_discounts.toFixed(2)}</td></tr>
            <tr><td>Imponibile:</td><td style="text-align:right">€ ${data.imponibile.toFixed(2)}</td></tr>
            <tr><td>IVA (${(data.vat_rate * 100).toFixed(0)}%):</td><td style="text-align:right">€ ${data.iva.toFixed(2)}</td></tr>
        </table>
        <div class="total">TOTALE € ${data.total_amount.toFixed(2)}</div>
        ${c.transmission_id ? `<div class="footer">Trasmesso ADE<br/>ID: ${c.transmission_id}<br/>${new Date(c.sent_at).toLocaleString("it-IT")}</div>` : ""}
        </body></html>`);
        win.document.close();
    };

    return (
        <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4 backdrop-blur-sm overflow-y-auto" onClick={onClose}>
            <div onClick={(e) => e.stopPropagation()} className="bg-surface border border-[#1f2c44] rounded-2xl p-6 max-w-2xl w-full my-8" data-testid="closure-modal">
                <div className="flex items-center justify-between mb-5">
                    <div className="flex items-center gap-2">
                        <Receipt className="w-5 h-5 text-brand" />
                        <div className="font-display text-2xl font-bold">Chiusura Corrispettivi</div>
                    </div>
                    <button onClick={onClose} className="text-elegant hover:text-warm" data-testid="close-closure"><X /></button>
                </div>

                <div className="flex items-center gap-3 mb-4">
                    <input type="date" value={date} onChange={(e) => setDate(e.target.value)} data-testid="closure-date"
                        className="h-11 px-3 bg-surface-elevated border border-[#1f2c44] rounded-md focus:border-brand focus:outline-none" />
                    <button onClick={() => loadPreview(date)} className="h-11 px-4 border border-[#1f2c44] hover:border-brand text-elegant hover:text-brand rounded-md flex items-center gap-1 text-xs">
                        <RefreshCw className={`w-3 h-3 ${loading ? "animate-spin" : ""}`} /> Aggiorna
                    </button>
                </div>

                {loading && <div className="text-elegant text-center py-6">Carico riepilogo...</div>}

                {preview && !loading && (
                    <>
                        {preview.already_closed && (
                            <div className="bg-amber-900/30 border border-amber-700/50 rounded-md p-3 mb-3 text-sm text-amber-300" data-testid="already-closed-warn">
                                ⚠ Già chiusa: <strong>Z{preview.closure.z_number}</strong> · trasmessa {new Date(preview.closure.sent_at).toLocaleString("it-IT")}
                            </div>
                        )}

                        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
                            <Stat label="Totale" value={`€ ${preview.summary.total_amount.toFixed(2)}`} accent="text-brand text-xl" testId="closure-total" />
                            <Stat label="Documenti" value={preview.summary.documents_count} />
                            <Stat label="Imponibile" value={`€ ${preview.summary.imponibile.toFixed(2)}`} />
                            <Stat label={`IVA ${(preview.summary.vat_rate * 100).toFixed(0)}%`} value={`€ ${preview.summary.iva.toFixed(2)}`} />
                        </div>

                        <div className="bg-deep border border-[#1f2c44] rounded-md p-3 mb-4">
                            <div className="text-[10px] uppercase tracking-wider text-elegant mb-2">Per metodo di pagamento</div>
                            {preview.by_method.length === 0 ? (
                                <div className="text-elegant text-sm text-center py-2">Nessun pagamento.</div>
                            ) : (
                                <div className="space-y-1">
                                    {preview.by_method.map((m) => (
                                        <div key={m.method} className="flex items-center justify-between text-sm">
                                            <span className="text-warm capitalize">{m.method}</span>
                                            <span className="text-elegant text-xs">{m.count} op.</span>
                                            <span className="font-display font-bold text-brand">€ {m.amount.toFixed(2)}</span>
                                        </div>
                                    ))}
                                </div>
                            )}
                            <div className="border-t border-[#1f2c44] mt-2 pt-2 grid grid-cols-3 gap-2 text-[11px] text-elegant">
                                <span>Scontrini: <strong className="text-warm">{preview.summary.scontrini_count}</strong></span>
                                <span>Fatture: <strong className="text-warm">{preview.summary.fatture_count}</strong></span>
                                <span>Sconti: <strong className="text-warm">€ {preview.summary.total_discounts.toFixed(2)}</strong></span>
                            </div>
                        </div>

                        <textarea value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Note (opzionale)" data-testid="closure-notes"
                            className="w-full h-20 px-3 py-2 bg-surface-elevated border border-[#1f2c44] rounded-md text-sm focus:border-brand focus:outline-none mb-4" />

                        <div className="flex gap-2 flex-wrap">
                            <button onClick={() => closeDay(false)} disabled={closing || preview.summary.total_amount === 0} data-testid="execute-closure"
                                className="flex-1 h-14 bg-brand hover:bg-brand-light text-deep font-bold uppercase tracking-wider rounded-md flex items-center justify-center gap-2 disabled:opacity-40">
                                <Send className={`w-5 h-5 ${closing ? "animate-pulse" : ""}`} />
                                {closing ? "Trasmissione..." : preview.already_closed ? "Ri-emetti chiusura" : "Esegui chiusura & invia"}
                            </button>
                            <button onClick={printReceipt} disabled={!preview.summary.total_amount} data-testid="print-closure"
                                className="h-14 px-5 border border-brand text-brand rounded-md flex items-center gap-2 hover:bg-brand-soft disabled:opacity-40">
                                <Receipt className="w-5 h-5" /> Stampa
                            </button>
                        </div>
                    </>
                )}

                <div className="border-t border-[#1f2c44] mt-5 pt-4">
                    <button onClick={() => setShowHistory(!showHistory)} className="text-xs uppercase tracking-wider text-elegant hover:text-brand flex items-center gap-1" data-testid="toggle-history">
                        {showHistory ? "▼" : "▶"} Storico chiusure ({history.length})
                    </button>
                    {showHistory && (
                        <div className="mt-3 space-y-1 max-h-60 overflow-y-auto" data-testid="closure-history">
                            {history.length === 0 ? (
                                <div className="text-elegant text-xs text-center py-3">Nessuna chiusura precedente.</div>
                            ) : history.map((c) => (
                                <div key={c.id} className="flex items-center justify-between bg-deep border border-[#1f2c44] rounded p-2 text-sm">
                                    <span className="font-mono text-brand font-bold">Z{c.z_number}</span>
                                    <span className="text-warm">{c.date}</span>
                                    <span className="font-display font-bold">€ {c.summary.total_amount.toFixed(2)}</span>
                                    <span className="text-[10px] text-elegant">{c.operator_name}</span>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

const QuickSaleModal = ({ onClose, onPaid }) => {
    const [tab, setTab] = useState("products"); // products | manual
    const [menu, setMenu] = useState({ categories: [], items: [] });
    const [activeCat, setActiveCat] = useState(null);
    const [search, setSearch] = useState("");
    const [cart, setCart] = useState([]); // {id, name, price, quantity}
    const [manualName, setManualName] = useState("");
    const [manualPrice, setManualPrice] = useState("");
    const [manualQty, setManualQty] = useState(1);
    const [discountMode, setDiscountMode] = useState("pct");
    const [discountPct, setDiscountPct] = useState(0);
    const [discountAmt, setDiscountAmt] = useState(0);
    const [method, setMethod] = useState("contanti");
    const [docType, setDocType] = useState("scontrino");
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        api.get("/menu").then(({ data }) => {
            setMenu(data);
            if (data.categories?.length) setActiveCat(data.categories[0].id);
        }).catch(() => { /* */ });
    }, []);

    const visibleItems = useMemo(() => {
        const items = menu.items || [];
        if (search.trim()) {
            const q = search.toLowerCase();
            return items.filter((it) => it.name.toLowerCase().includes(q));
        }
        if (activeCat) return items.filter((it) => it.category_id === activeCat);
        return items;
    }, [menu, activeCat, search]);

    const addItem = (item) => {
        setCart((prev) => {
            const existing = prev.find((c) => c.id === item.id);
            if (existing) return prev.map((c) => c.id === item.id ? { ...c, quantity: c.quantity + 1 } : c);
            return [...prev, { id: item.id, menu_item_id: item.id, name: item.name, price: item.price, quantity: 1 }];
        });
    };

    const addManual = () => {
        if (!manualName.trim() || !parseFloat(manualPrice)) return;
        const id = `manual-${Date.now()}`;
        setCart((prev) => [...prev, { id, name: manualName.trim(), price: parseFloat(manualPrice), quantity: parseInt(manualQty) || 1 }]);
        setManualName(""); setManualPrice(""); setManualQty(1);
    };

    const updateQty = (id, delta) => {
        setCart((prev) => prev.map((c) => c.id === id ? { ...c, quantity: Math.max(0, c.quantity + delta) } : c).filter((c) => c.quantity > 0));
    };
    const removeItem = (id) => setCart((prev) => prev.filter((c) => c.id !== id));
    const clearCart = () => setCart([]);

    const subtotal = cart.reduce((s, c) => s + c.price * c.quantity, 0);
    const discAmt = discountMode === "pct" ? subtotal * (discountPct / 100) : Math.min(subtotal, discountAmt);
    const total = Math.max(0, subtotal - discAmt);

    const sell = async () => {
        if (cart.length === 0) { toast.error("Aggiungi almeno un prodotto"); return; }
        setSubmitting(true);
        try {
            const { data } = await api.post("/payments/quick-sale", {
                items: cart.map((c) => ({ menu_item_id: c.menu_item_id, name: c.name, price: c.price, quantity: c.quantity })),
                discount_pct: discountMode === "pct" ? discountPct : 0,
                discount_amt: discountMode === "amt" ? discountAmt : 0,
                method,
                document_type: docType,
            });
            toast.success(`${docType === "fattura" ? "Fattura" : "Scontrino"} ${data.document.number} emesso · € ${data.total.toFixed(2)}`);
            onPaid?.();
            onClose();
        } catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
        setSubmitting(false);
    };

    return (
        <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4 backdrop-blur-sm overflow-y-auto" onClick={onClose}>
            <div onClick={(e) => e.stopPropagation()} className="bg-surface border border-[#1f2c44] rounded-2xl p-6 max-w-5xl w-full my-4" data-testid="quick-sale-modal">
                <div className="flex items-center justify-between mb-5">
                    <div className="flex items-center gap-2">
                        <Coffee className="w-5 h-5 text-amber-500" />
                        <div className="font-display text-2xl font-bold">Conto Libero · Banco</div>
                        <div className="text-elegant text-xs">scontrino senza tavolo</div>
                    </div>
                    <button onClick={onClose} className="text-elegant hover:text-warm" data-testid="close-quick-sale"><X /></button>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
                    {/* LEFT: Picker */}
                    <div className="bg-deep border border-[#1f2c44] rounded-md p-4">
                        <div className="flex border border-[#1f2c44] rounded overflow-hidden mb-3">
                            <button onClick={() => setTab("products")} data-testid="tab-products"
                                className={`flex-1 h-10 text-xs uppercase tracking-wider flex items-center justify-center gap-1 ${tab === "products" ? "bg-brand text-deep font-bold" : "text-elegant"}`}>
                                <ShoppingBag className="w-4 h-4" /> Prodotti Menu
                            </button>
                            <button onClick={() => setTab("manual")} data-testid="tab-manual"
                                className={`flex-1 h-10 text-xs uppercase tracking-wider flex items-center justify-center gap-1 ${tab === "manual" ? "bg-brand text-deep font-bold" : "text-elegant"}`}>
                                <Calculator className="w-4 h-4" /> Articolo libero
                            </button>
                        </div>

                        {tab === "products" && (
                            <>
                                <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Cerca prodotto..." data-testid="qs-search"
                                    className="w-full h-10 px-3 mb-3 bg-surface-elevated border border-[#1f2c44] rounded text-sm focus:border-brand focus:outline-none" />
                                {!search && (
                                    <div className="flex gap-1 mb-3 overflow-x-auto pb-1">
                                        {(menu.categories || []).map((c) => (
                                            <button key={c.id} onClick={() => setActiveCat(c.id)}
                                                className={`px-3 h-9 rounded-md text-xs uppercase tracking-wider whitespace-nowrap ${activeCat === c.id ? "bg-brand text-deep font-bold" : "border border-[#1f2c44] text-elegant"}`}
                                                data-testid={`qs-cat-${c.id}`}>{c.name}</button>
                                        ))}
                                    </div>
                                )}
                                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 max-h-96 overflow-y-auto" data-testid="qs-items">
                                    {visibleItems.map((it) => (
                                        <button key={it.id} onClick={() => addItem(it)} data-testid={`qs-add-${it.id}`}
                                            className="bg-surface-elevated border border-[#1f2c44] hover:border-brand hover:bg-brand-soft rounded p-3 text-left transition-colors">
                                            <div className="font-medium text-sm text-warm leading-tight">{it.name}</div>
                                            <div className="font-display font-bold text-brand mt-1">€ {it.price.toFixed(2)}</div>
                                        </button>
                                    ))}
                                    {visibleItems.length === 0 && (
                                        <div className="col-span-3 text-elegant text-center py-6 text-sm">Nessun prodotto</div>
                                    )}
                                </div>
                            </>
                        )}

                        {tab === "manual" && (
                            <div className="space-y-3">
                                <div className="text-xs text-elegant">Inserisci manualmente prezzo e descrizione (es. caffè non a menu).</div>
                                <input value={manualName} onChange={(e) => setManualName(e.target.value)} placeholder="Descrizione" data-testid="qs-manual-name"
                                    className="w-full h-11 px-3 bg-surface-elevated border border-[#1f2c44] rounded text-sm focus:border-brand focus:outline-none" />
                                <div className="grid grid-cols-2 gap-2">
                                    <input type="number" step="0.10" min="0" value={manualPrice} onChange={(e) => setManualPrice(e.target.value)} placeholder="Prezzo €" data-testid="qs-manual-price"
                                        className="h-11 px-3 bg-surface-elevated border border-[#1f2c44] rounded text-right focus:border-brand focus:outline-none" />
                                    <input type="number" min="1" value={manualQty} onChange={(e) => setManualQty(e.target.value)} placeholder="Quantità" data-testid="qs-manual-qty"
                                        className="h-11 px-3 bg-surface-elevated border border-[#1f2c44] rounded text-right focus:border-brand focus:outline-none" />
                                </div>
                                <button onClick={addManual} data-testid="qs-manual-add"
                                    disabled={!manualName.trim() || !parseFloat(manualPrice)}
                                    className="w-full h-12 bg-amber-600 hover:bg-amber-500 text-white font-bold rounded disabled:opacity-40">
                                    <Plus className="inline w-4 h-4 mr-1" /> Aggiungi al conto
                                </button>
                                <div className="text-[10px] text-elegant pt-2 border-t border-[#1f2c44]">
                                    Suggerimento: usa "Articolo libero" solo per prodotti non gestiti a magazzino.
                                </div>
                            </div>
                        )}
                    </div>

                    {/* RIGHT: Cart + Pay */}
                    <div className="space-y-3">
                        <div className="bg-deep border border-[#1f2c44] rounded-md p-4">
                            <div className="flex items-center justify-between mb-2">
                                <div className="text-[10px] uppercase tracking-wider text-brand">Conto</div>
                                {cart.length > 0 && (
                                    <button onClick={clearCart} className="text-[10px] text-red-400 hover:text-red-300 uppercase tracking-wider">Svuota</button>
                                )}
                            </div>
                            <div className="space-y-2 max-h-56 overflow-y-auto" data-testid="qs-cart">
                                {cart.length === 0 && <div className="text-elegant text-sm py-4 text-center">Aggiungi prodotti dal menu</div>}
                                {cart.map((c) => (
                                    <div key={c.id} className="flex items-center justify-between gap-2 bg-surface-elevated border border-[#1f2c44] rounded p-2">
                                        <div className="flex-1 min-w-0">
                                            <div className="font-medium text-sm truncate">{c.name}</div>
                                            <div className="text-[10px] text-elegant">€ {c.price.toFixed(2)} cad.</div>
                                        </div>
                                        <div className="flex items-center gap-1">
                                            <button onClick={() => updateQty(c.id, -1)} className="w-7 h-7 rounded bg-deep hover:bg-[#1f2c44] flex items-center justify-center"><Minus className="w-3 h-3" /></button>
                                            <span className="font-display font-bold w-6 text-center">{c.quantity}</span>
                                            <button onClick={() => updateQty(c.id, 1)} className="w-7 h-7 rounded bg-deep hover:bg-[#1f2c44] flex items-center justify-center"><Plus className="w-3 h-3" /></button>
                                            <button onClick={() => removeItem(c.id)} className="w-7 h-7 rounded text-red-400 hover:bg-red-900/20 flex items-center justify-center"><Trash2 className="w-3 h-3" /></button>
                                        </div>
                                        <div className="font-display font-bold text-brand whitespace-nowrap min-w-[60px] text-right">€ {(c.price * c.quantity).toFixed(2)}</div>
                                    </div>
                                ))}
                            </div>

                            {cart.length > 0 && (
                                <div className="border-t border-[#1f2c44] mt-3 pt-3 space-y-2 text-sm">
                                    <div className="flex justify-between text-elegant"><span>Subtotale</span><span>€ {subtotal.toFixed(2)}</span></div>
                                    <div className="flex items-center gap-2">
                                        <div className="flex border border-[#1f2c44] rounded overflow-hidden">
                                            <button onClick={() => setDiscountMode("pct")} className={`px-3 h-8 text-[10px] ${discountMode === "pct" ? "bg-brand text-deep" : "text-elegant"}`}>%</button>
                                            <button onClick={() => setDiscountMode("amt")} className={`px-3 h-8 text-[10px] ${discountMode === "amt" ? "bg-brand text-deep" : "text-elegant"}`}>€</button>
                                        </div>
                                        <input type="number" min="0" step="0.5"
                                            value={discountMode === "pct" ? discountPct : discountAmt}
                                            onChange={(e) => discountMode === "pct" ? setDiscountPct(parseFloat(e.target.value) || 0) : setDiscountAmt(parseFloat(e.target.value) || 0)}
                                            data-testid="qs-discount"
                                            className="flex-1 h-8 px-2 bg-surface-elevated border border-[#1f2c44] rounded text-right" />
                                        <span className="text-elegant text-xs">- € {discAmt.toFixed(2)}</span>
                                    </div>
                                    <div className="flex justify-between font-display text-2xl font-bold pt-2 border-t border-[#1f2c44]">
                                        <span>Totale</span><span className="text-brand" data-testid="qs-total">€ {total.toFixed(2)}</span>
                                    </div>
                                </div>
                            )}
                        </div>

                        <div className="bg-deep border border-[#1f2c44] rounded-md p-4 space-y-3">
                            <div>
                                <div className="text-[10px] uppercase tracking-wider text-brand mb-2">Pagamento</div>
                                <div className="grid grid-cols-3 gap-1">
                                    {["contanti", "carta", "bancomat", "satispay", "ticket", "misto"].map((m) => (
                                        <button key={m} onClick={() => setMethod(m)} data-testid={`qs-method-${m}`}
                                            className={`h-10 rounded text-xs capitalize ${method === m ? "bg-brand text-deep font-bold" : "border border-[#1f2c44] text-elegant"}`}>{m}</button>
                                    ))}
                                </div>
                            </div>
                            <div>
                                <div className="text-[10px] uppercase tracking-wider text-brand mb-2">Documento</div>
                                <div className="grid grid-cols-2 gap-2">
                                    <button onClick={() => setDocType("scontrino")} data-testid="qs-doc-scontrino"
                                        className={`h-10 rounded text-xs uppercase tracking-wider flex items-center justify-center gap-1 ${docType === "scontrino" ? "bg-brand text-deep font-bold" : "border border-[#1f2c44] text-elegant"}`}>
                                        <Receipt className="w-3 h-3" /> Scontrino
                                    </button>
                                    <button onClick={() => setDocType("fattura")} data-testid="qs-doc-fattura"
                                        className={`h-10 rounded text-xs uppercase tracking-wider flex items-center justify-center gap-1 ${docType === "fattura" ? "bg-brand text-deep font-bold" : "border border-[#1f2c44] text-elegant"}`}>
                                        Fattura
                                    </button>
                                </div>
                            </div>
                            <button onClick={sell} disabled={submitting || cart.length === 0} data-testid="qs-sell"
                                className="w-full h-14 bg-brand hover:bg-brand-light text-deep font-bold uppercase tracking-wider rounded disabled:opacity-40">
                                {submitting ? "Emissione..." : `Incassa € ${total.toFixed(2)}`}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

const Stat = ({ label, value, accent = "text-warm", testId }) => (
    <div className="bg-deep border border-[#1f2c44] rounded-md p-3" data-testid={testId}>
        <div className="text-[10px] uppercase tracking-wider text-elegant">{label}</div>
        <div className={`font-display font-bold ${accent}`}>{value}</div>
    </div>
);

export default TerminaleCassa;
